import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { miniSaasProjects } from '../data/projects';
import { supabase } from '../supabase/supabase';

const { FiZap, FiCoffee, FiShoppingBag, FiHeart, FiMenu, FiX, FiLock, FiMail, FiArrowRight, FiArrowUp, FiStar } = FiIcons;

// Get all featured projects for the ticker
const featuredTools = miniSaasProjects.filter(p => p.featured);

// Build ticker items: featured tools + static promo items
const tickerItems = [
  ...featuredTools.map(p => ({ label: 'FEATURED', name: p.name, color: 'bg-purple-600' })),
  { label: 'NEW', name: 'Coverlab', color: 'bg-pink-500' },
  { label: 'HOT', name: 'Vidroid', color: 'bg-yellow-500' },
  { label: 'FREE', name: 'GPT5 Prompts', color: 'bg-emerald-500' },
  { label: 'TOOL', name: 'AI Storyboard', color: 'bg-indigo-500' },
  { label: 'NEW', name: 'PDFly', color: 'bg-blue-500' },
];

// Deduplicate by name
const uniqueTickerItems = tickerItems.filter(
  (item, index, self) => index === self.findIndex(t => t.name === item.name)
);

// Triple the array so the seamless loop works perfectly
const loopItems = [...uniqueTickerItems, ...uniqueTickerItems, ...uniqueTickerItems];

const BannerTicker = () => {
  const itemWidth = 160; // approximate px per item
  const totalItems = uniqueTickerItems.length;
  const singleSetWidth = totalItems * itemWidth;

  return (
    <div className="relative z-[60] overflow-hidden h-10 bg-[#07000f] border-b border-white/5">
      <div className="absolute left-0 top-0 h-full w-16 bg-gradient-to-r from-[#07000f] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 h-full w-16 bg-gradient-to-l from-[#07000f] to-transparent z-10 pointer-events-none" />

      <motion.div
        className="flex items-center h-full whitespace-nowrap"
        animate={{ x: [0, -singleSetWidth] }}
        transition={{
          x: {
            duration: totalItems * 3.5,
            repeat: Infinity,
            ease: 'linear',
            repeatType: 'loop',
          },
        }}
      >
        {loopItems.map((item, i) => (
          <div
            key={i}
            className="inline-flex items-center gap-2 px-6 flex-shrink-0"
            style={{ minWidth: `${itemWidth}px` }}
          >
            <span className={`${item.color} text-white text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded flex-shrink-0`}>
              {item.label}
            </span>
            <span className="text-white/80 font-semibold text-xs tracking-wide truncate">
              {item.name}
            </span>
            <span className="text-white/20 text-xs flex-shrink-0">·</span>
          </div>
        ))}
      </motion.div>
    </div>
  );
};

const Layout = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Supabase Keep-Alive Ping (Strictly Every 24 hours)
  useEffect(() => {
    const pingSupabase = async () => {
      try {
        await supabase.from('keepalive_pings_1776800000001').insert([{ source: 'auto_ping' }]);
      } catch (err) {
        console.error('Keep-alive ping failed', err);
      }
    };
    
    pingSupabase(); // Ping on first load
    const interval = setInterval(pingSupabase, 24 * 60 * 60 * 1000); // 24 hours in milliseconds
    
    return () => clearInterval(interval);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const navLinks = [
    { name: 'Support Us', icon: FiCoffee, url: 'https://buymeacoffee.com/daddacool', primary: true, color: 'from-yellow-400 to-orange-500' },
    { name: 'Merch', icon: FiShoppingBag, url: 'https://shop.dadda.cool/', primary: false },
  ];

  return (
    <div className="min-h-screen bg-[#060010] flex flex-col font-sans text-white">
      <BannerTicker />

      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -inset-10 opacity-20">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-purple-500 rounded-full"
              initial={{ x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight }}
              animate={{ x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight }}
              transition={{ duration: Math.random() * 10 + 20, repeat: Infinity, repeatType: 'reverse' }}
            />
          ))}
        </div>
      </div>

      {/* Header */}
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 100 }}
        className="relative z-50 bg-[#060010]/80 backdrop-blur-xl border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <motion.div
                whileHover={{ rotate: 180 }}
                transition={{ duration: 0.5 }}
                className="w-10 h-10 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20"
              >
                <SafeIcon icon={FiZap} className="text-white text-xl" />
              </motion.div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">Fiverring</h1>
                <p className="text-[10px] text-white/40 uppercase tracking-widest hidden sm:block">Mini AI SaaS Discovery</p>
              </div>
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center space-x-3">
              <Link
                to="/reviews"
                className="flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-bold transition-all bg-white/5 text-white border border-white/10 hover:bg-white/10 hover:border-purple-500/50"
              >
                <SafeIcon icon={FiStar} className="text-lg" />
                <span>Reviews</span>
              </Link>
              {navLinks.map((link) => (
                <motion.a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-bold transition-all shadow-lg ${link.primary ? `bg-gradient-to-r ${link.color} text-black` : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'}`}
                >
                  <SafeIcon icon={link.icon} className="text-lg" />
                  <span>{link.name}</span>
                </motion.a>
              ))}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-white/70 hover:text-white bg-white/5 rounded-lg"
            >
              <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="text-2xl" />
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            className="md:hidden border-t border-white/5 bg-[#060010]/95 backdrop-blur-xl"
          >
            <div className="px-4 py-4 space-y-3">
              <Link
                to="/reviews"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center space-x-2 w-full px-4 py-3 rounded-xl text-sm font-bold bg-white/5 text-white border border-white/10 hover:bg-white/10 transition-all"
              >
                <SafeIcon icon={FiStar} className="text-lg" />
                <span>Reviews</span>
              </Link>
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-center space-x-2 w-full px-4 py-3 rounded-xl text-sm font-bold transition-all ${link.primary ? `bg-gradient-to-r ${link.color} text-black` : 'bg-white/5 text-white border border-white/10'}`}
                >
                  <SafeIcon icon={link.icon} className="text-lg" />
                  <span>{link.name}</span>
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </motion.header>

      <main className="flex-grow relative z-10 flex flex-col">{children}</main>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="relative z-10 bg-[#060010] border-t border-white/5 pt-10 pb-6"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            <Link
              to="/contact"
              className="group flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border border-purple-500/30 text-white font-bold text-sm hover:from-purple-600/30 hover:to-indigo-600/30 hover:border-purple-500/60 hover:shadow-lg hover:shadow-purple-500/20 transition-all"
            >
              <SafeIcon icon={FiMail} className="text-purple-400 group-hover:text-purple-300" /> Contact Us
            </Link>
            <a
              href="https://buymeacoffee.com/daddacool"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold text-sm hover:shadow-lg hover:shadow-yellow-500/30 hover:scale-105 transition-all"
            >
              <SafeIcon icon={FiCoffee} /> Support Us
            </a>
            <Link
              to="/admin/dashboard"
              className="group flex items-center gap-2 px-6 py-3 rounded-2xl bg-[#0c0518] border border-[#392e4e] text-white/60 font-bold text-sm hover:border-purple-500/40 hover:text-white hover:bg-[#150a28] transition-all"
            >
              <SafeIcon icon={FiLock} className="text-white/40 group-hover:text-purple-400" /> Admin
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center border-t border-white/5 pt-8">
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start space-x-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-md flex items-center justify-center">
                  <SafeIcon icon={FiZap} className="text-white text-xs" />
                </div>
                <span className="text-white font-bold">Fiverring</span>
              </div>
              <p className="text-white/40 text-sm">Curated AI micro-SaaS directory.</p>
            </div>

            <div className="flex flex-col items-center space-y-3">
              <div className="flex space-x-6 text-sm text-white/60">
                <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
                <Link to="/privacy-terms" className="hover:text-white transition-colors">Privacy & Terms</Link>
                <a href="https://shop.dadda.cool/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Merch</a>
              </div>
              <a
                href="https://buymeacoffee.com/daddacool"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-sm text-yellow-500 hover:text-yellow-400 transition-colors"
              >
                <SafeIcon icon={FiHeart} className="fill-current" />
                <span>Support Dadda Cool</span>
              </a>
            </div>

            <div className="text-center md:text-right space-y-2">
              <p className="text-white/50 text-sm bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent font-bold">
                Ai8848 all rights reserved
              </p>
              <div className="flex items-center justify-center md:justify-end space-x-4 text-white/30 text-xs">
                <div className="flex items-center space-x-2">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
                    className="w-3 h-3 border border-purple-500 border-t-transparent rounded-full"
                  />
                  <span>System Active</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.footer>

      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: 20 }}
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 z-[100] p-3 md:p-4 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-110 active:scale-95 transition-all border border-white/20"
          >
            <SafeIcon icon={FiArrowUp} className="text-xl" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout;